/* eslint-disable @typescript-eslint/naming-convention */
// should return an array of objects, based upon the supplied protocol's subinterface.
// each object should contain the field name, and the available options.
import { keyBy, mapValues } from "lodash";

export type ParameterOption = {
    key: string;
    value: number;
};

export type ProtocolInputParameter = {
    labelKey: string;
    fieldName: string;
    dataType: string;
    min?: number;
    max?: number;
    options?: ParameterOption[];
    defaultValue?: boolean | number;
};

function createNumericInputParameter(fieldName: string, min: number, max: number): ProtocolInputParameter {
    const labelKey = fieldName.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
    return {
        dataType: 'number',
        labelKey,
        fieldName,
        defaultValue: min,
        min,
        max,
    };
}

function createBooleanInputParameter(fieldName: string): ProtocolInputParameter {
    const labelKey = fieldName.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
    return {
        dataType: 'boolean',
        fieldName,
        labelKey,
        defaultValue: false,
    };
}

function createSelectInputParameter(fieldName: string, options: string[], defaultLabel?: string): ProtocolInputParameter {
    const labelKey = fieldName.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
    const defaultValue = defaultLabel ? options.indexOf(defaultLabel) || 0 : 0;
    
    const selectInputParam = {
        dataType: 'select',
        fieldName,
        labelKey,
        defaultValue,
        options: options
            .map((option, index) => {
                return {
                    key: option,
                    value: index,
                };
            })
            // any option with falsy key will be excluded from UI
            .filter(option => !!option.key),
    };
    
    return selectInputParam;
}

const parallelProtocolParameters = [
    createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
    createNumericInputParameter("offsetValue", -9, 9),
    createSelectInputParameter("firstVisualization", [ "BLANK", "ZERO" ]),
    createSelectInputParameter("commonSelection", [ "NEGATIVE", "POSITIVE" ], "POSITIVE"),
    createSelectInputParameter("arrowType", [ "FIXED_ARROWS", "SCROLLING_ARROWS" ]),
    createSelectInputParameter("enableAuxSignals", [ "10_PLUS_0", "6_PLUS_4" ]),
    createNumericInputParameter("inputFiltering", 0, 20),
    createBooleanInputParameter("gongFromArrows"),
    createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
    createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
];

const inputParameters = {
    //
    // RS485
    //
    "DMG_RS485": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter('arrowsConfiguration', [ 'DIRECTION', 'NEXT_DIRECTION' ]),
        createBooleanInputParameter('carAtFloor'),
        createBooleanInputParameter('gongWithoutArrow'),
        createBooleanInputParameter('convertMezzanineFormat'),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],
    "DMG_EQ": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter('arrowsConfiguration', [ 'DIRECTION', 'NEXT_DIRECTION' ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("selectedCabin", 0, 3),
        createNumericInputParameter("selectedFloor", 0, 31),
    ],
    "DELBO": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],
    "KW": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("displayAddress", 0, 2),
    ],

    //
    // Parallel
    //
    "1XP": parallelProtocolParameters,
    "1XS": parallelProtocolParameters,
    "GRAY": parallelProtocolParameters,
    "BINARY": parallelProtocolParameters,
    "SENSORMANUAL": parallelProtocolParameters,
    "SENSORAUTO": parallelProtocolParameters,
    "TKE": parallelProtocolParameters,
    "MEA_1": parallelProtocolParameters, // MEA_QTouch
    "AUTINOR": parallelProtocolParameters,
    "MEA_2": parallelProtocolParameters, // MEA_NG120
    "3WIRE": parallelProtocolParameters,

    //
    // KINDS
    //
    "KINDS_STD": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createNumericInputParameter("offsetValue", -9, 9),
        createSelectInputParameter("firstVisualization", [ "BLANK", "ZERO" ]),
        createBooleanInputParameter("gongFromArrows"),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],

    //
    // OTIS
    //
    "OTIS_STD": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createBooleanInputParameter("gongFromArrows"),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],

    //
    // NEWLIFT
    //
    "NEWLIFT": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],

    //
    // SCH_FLOOR_GRAY
    //
    "SCH_FLOOR_GRAY": [
        createNumericInputParameter("offsetValue", -9, 9),
        createSelectInputParameter("firstVisualization", [ "BLANK", "ZERO" ]),
        createBooleanInputParameter("gongFromArrows"),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],

    //
    // FIGS_BIOBUS
    //
    "BIOBUS": [
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],

    //
    // CAN
    //
    "CAN_DMG": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter('arrowsConfiguration', [ 'DIRECTION', 'NEXT_DIRECTION' ]),
        createBooleanInputParameter('carAtFloor'),
        createBooleanInputParameter('gongWithoutArrow'),
        createBooleanInputParameter('convertMezzanineFormat'),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createSelectInputParameter("canBaudRate", [ "250K", "125K", "10K", "AUTO" ]),
        createNumericInputParameter("lineAddress", 0, 7),
    ],
    "CAN_IGV": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("lineAddress", 0, 7),
        createNumericInputParameter("displayAddress", 0, 31),
    ],
    "CAN_MOVILIFT": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],
    // TODO: CAN_TRESA

    //
    // CAN_V2
    //
    "CAN_DMG_V2": [ // Pitagora 4
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter('arrowsConfiguration', [ 'DIRECTION', 'NEXT_DIRECTION' ]),
        createBooleanInputParameter('carAtFloor'),
        createBooleanInputParameter('convertMezzanineFormat'),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createSelectInputParameter("baudRate", [ "50K", "100K", "125K", "250K", "AUTOBAUDING" ]),
        createNumericInputParameter("shaftId", 0, 6),
        createNumericInputParameter("displayFloor", 0, 31),
        createSelectInputParameter("displaySide", [ "SIDE_A", "SIDE_B" ]),
    ],
    "CAN_EC": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("displayAddress", 0, 128),
        createSelectInputParameter("frontOrRear", [ "FRONT", "REAR" ]),
        createSelectInputParameter("fireServiceMode", [ "OFF", "ON" ]),
        createSelectInputParameter("messageBlocking", [ "OFF", "ON" ]),
        createSelectInputParameter("fireAltMode", [ "OFF", "ON" ]),
    ],
    "CAN_EDEL": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createNumericInputParameter("offsetValue", -9, 9),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("displayAddress", 0, 31),
        createSelectInputParameter('arrowsConfiguration', [ 'DIRECTION', 'NEXT_DIRECTION' ]),
        createNumericInputParameter("selectedFloor", 0, 31),
    ],
    "CAN_GAL": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createSelectInputParameter("fireServiceMode", [ "OFF", "ON" ]),
    ],
    "CAN_JRT": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createSelectInputParameter("canBaudRate", [ "117K", "250K" ]),
        createNumericInputParameter("displayAddress", 1, 63),
        createSelectInputParameter("cabOrArr", [ "ARR", "CAB" ]),
        createSelectInputParameter("frontOrRear", [ "FRONT", "REAR" ]),
        createSelectInputParameter("fireServiceMode", [ "OFF", "ON" ]),
    ],
    "CAN_KOMP": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("displayAddress", 0, 15),
        createSelectInputParameter("hideVelocity", [ "SHOW", "HIDE" ]),
        createSelectInputParameter("hideClosingTime", [ "SHOW", "HIDE" ]),
    ],
    "CAN_MACPUAR": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createNumericInputParameter("offsetValue", -9, 9),
        createSelectInputParameter("firstVisualization", [ "BLANK", "ZERO" ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("liftNumber", 1, 255),
        createSelectInputParameter("doorSide", [ "SIDE1", "SIDE2", "SIDE3", "ALL_SIDES" ]),
        createSelectInputParameter("displaySequence", [ "ASCII_SEQUENCE", "LEVEL_SEQUENCE" ]),
    ],
    "CAN_STEP": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],
    "CAN_TKEUS": [ // Memphis
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createNumericInputParameter("offsetValue", -9, 9),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("displayAddress", 0, 4),
        createNumericInputParameter("carNumber", 0, 15),
        createBooleanInputParameter("oeoEnable"),
    ],
    "CAN_TKNE": [ // Northern
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createNumericInputParameter("offsetValue", -9, 9),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createSelectInputParameter("blankSettings", [ "NONE", "BLANK1", "BLANK2", "BOTH" ]),
    ],
    "CAN_SMARTRISE": [
        createSelectInputParameter("displayConfiguration", [ "COP", "LIPLOP", "SR_HALL" ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("displayFloor", 0, 127),
        createSelectInputParameter("frontOrRear", [ "FRONT", "REAR" ]),
    ],
    "CAN_ALPHA": [
        createSelectInputParameter("displayConfiguration", [ "COP", "LIPLOP" ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createSelectInputParameter("frontOrRear", [ "FRONT", "REAR" ]),
    ],
    "CAN_SEA": [
        createSelectInputParameter("displayConfiguration", [ "COP", "LIPLOP" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],
    "CAN_LIFTING": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],

    "SCH_CAR": [
        createSelectInputParameter("displayConfiguration", [ "COP", "LIPLOP" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("nodeId", 0, 255),
        createSelectInputParameter("canOpenBitrate", [ "1000K", "800K", "500K", "250K", "125K", '', "50K", "20K", "10K", "AUTOBAUDING" ]),
    ],
    /* TODO
    "CAN Open 417": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createSelectInputParameter("firefightersOperation", [ "DISABLED", "EGRESS_FLOOR", "NON_EGRESS_FLOOR" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createNumericInputParameter("nodeId", 16, 84),
        createNumericInputParameter("liftNumber", 1, 8),
        createNumericInputParameter("floorNumber", 0, 64),
        createNumericInputParameter("doorMask", 1, 15),
        createNumericInputParameter("doorSide", 1, 2),
        createNumericInputParameter("liftNumber", 1, 8)
    ],
    */

    //
    // FIGS_CAN
    //
    "FIGS_CAN": [ // SCH_CAN
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createSelectInputParameter("copNumber", [ "COP1", "COP2" ]),
        createSelectInputParameter("copDisplayType", [ "CPID", "ACID" ]),
    ],

    //
    // MX_BASIC
    //
    "MX_BASIC": [ // SCH_CAB (Gray)
        createNumericInputParameter("offsetValue", -9, 9),
        createSelectInputParameter("firstVisualization", [ "BLANK", "ZERO" ]),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
    ],

    //
    // APS
    //
    "APS": [
        createSelectInputParameter('displayConfiguration', [ 'COP', 'LIPLOP' ]),
        createNumericInputParameter("offsetValue", -9, 9),
        createSelectInputParameter("screensaverTimer", [ "DISABLED", "10_TO_20_MIN", "30_TO_60_MIN", "60_TO_120_MIN" ]),
        createSelectInputParameter("baudRate", [ "50K", "125K", "250K" ]),
        createSelectInputParameter("resetInput", [ "NO_CONTACT", "NC_CONTACT", "MAIN_FLOOR_BUTTON" ]),
        createSelectInputParameter("doorZoneInput", [ "SINGLE_INPUT", "DOUBLE_INPUT" ]),
        createNumericInputParameter("velocityThreshold", 5, 25),
    ],
} as Record<string, ProtocolInputParameter[]>;

const copModeDisabledParameters = [ 'firefightersOperation', 'fireServiceMode', 'fireAltMode' ];

export function getDefaultParamsBySubinterface(subinterface: string | null): Record<string, string | number | boolean | undefined> {
    const inputParameters = getInputParametersBySubInterface(subinterface);
    return mapValues(keyBy(inputParameters, 'fieldName'), param => param.defaultValue);
}

export function getInputParametersBySubInterface(subInterface: string | null, configParams?: Record<string, string | number | boolean>): ProtocolInputParameter[] | undefined {
    if (!subInterface) {
        return undefined;
    }
    const baseParams = inputParameters[subInterface] || inputParameters[subInterface.toUpperCase()];
    const isLipLopConfig = !!(configParams && configParams['displayConfiguration'] === 1);
    return baseParams ? (isLipLopConfig ? baseParams : baseParams.filter(param => !copModeDisabledParameters.includes(param.fieldName))) : undefined;
}
